import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Input } from '../../input';
import { Textarea } from '../../textarea';
import { Button } from '../../button/button';
import { X } from 'lucide-react';
import HelpIcon from '../../help-icon';
import { FormAccordions } from '../../form-accordions';
import evaluatorBuilderData from '../../../../data/evaluatorBuilder/data.json';
import type { EvaluatorFormProps } from './types';
import { DescriptionBox } from '../../description-box';
import { SectorSelect } from '../../sector-select/sector-select';

export function EvaluatorForm({
  editingEvaluator,
  evaluatorName,
  evaluatorStatement,
  onNameChange,
  onStatementChange,
  isSaving,
  onCancelEdit,
  onSave,
  isTemplate,
  sectorId,
  onSectorChange,
}: EvaluatorFormProps): JSX.Element {
  return (
    <div className="lg:w-1/2" data-onboarding="evaluator-form">
      <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
            {editingEvaluator ? 'Edit evaluator' : 'Create new evaluator'}
          </CardTitle>
          {editingEvaluator && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onCancelEdit}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <DescriptionBox text={evaluatorBuilderData.introText.text} />
          <p className="!mt-0 p-0 text-sm italic text-prompt-safecomabbey">
            {evaluatorBuilderData.introText.note}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                {evaluatorBuilderData.evaluatorName.label}
              </label>
              <HelpIcon content={evaluatorBuilderData.evaluatorName.tooltip} />
            </div>
            <Input
              value={evaluatorName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder={evaluatorBuilderData.evaluatorName.placeholder}
              className="bg-prompt-safecomwhite"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                {evaluatorBuilderData.evaluatorCriteria.label}
              </label>
              <HelpIcon
                content={evaluatorBuilderData.evaluatorCriteria.tooltip}
              />
            </div>
            <Textarea
              value={evaluatorStatement}
              onChange={(e) => onStatementChange(e.target.value)}
              className="min-h-[200px] bg-prompt-safecomwhite"
              placeholder={evaluatorBuilderData.evaluatorCriteria.placeholder}
            />
          </div>

          {isTemplate && onSectorChange && (
            <SectorSelect value={sectorId ?? null} onChange={onSectorChange} />
          )}

          <FormAccordions
            tipsAccordion={evaluatorBuilderData.tipsAccordion}
            exampleAccordion={evaluatorBuilderData.exampleAccordion}
          />

          <div className="flex justify-end gap-2 pt-4">
            {editingEvaluator && (
              <Button
                onClick={onCancelEdit}
                variant="outline"
                className="rounded-[50px] border-prompt-safecomoutrageous-orange px-8 text-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy hover:text-prompt-safecomoutrageous-orange"
                disabled={isSaving}
              >
                Cancel
              </Button>
            )}
            <Button
              data-onboarding="evaluator-save"
              onClick={onSave}
              className="hover:bg-prompt-safecomoutrageous-orange/90 rounded-[50px] bg-prompt-safecomoutrageous-orange px-8 text-prompt-safecomwhite"
              disabled={isSaving}
            >
              {isSaving
                ? 'Saving...'
                : editingEvaluator
                  ? 'Update evaluator'
                  : 'Create evaluator'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
